import React from "react"

import { textColor } from "../../utilities/color/textColor"
import { Icon } from "../icon/Icon"
import { IconSize } from "../icon/IconSize"
import { LayoutSize } from "../layout/flex/LayoutSizeContext"
import { Translator } from "../i18n/Translator"
import { withTranslator } from "../i18n/withTranslator"
import { withLayoutSize } from "../layout/flex/withLayoutSize"
import { GridDiagramCellConfig } from "./GridDiagramCellConfig"
import { isGridDiagramCellColor } from "./GridDiagramCellColor"
import { TestProps } from "../helpers/TestProps"
import { renderTestID } from "../helpers/renderTestID"
import { GridDiagramCellInfo } from "./GridDiagramCellInfo"
import { generateID } from "../../utilities/uuid"

export interface GridDiagramCellProps extends GridDiagramCellInfo, TestProps {

  /**
   * The size used to display contents of the GridCell
   */
  size?: GridDiagramCellSize

  /**
   * Handler to be called when the checkbox is clicked on.
   */
  onSelect?: () => void

  /**
   * Translator is used to translate aria labels.
   *
   * Injected automatically.
   */
  translator: Translator

  /**
   * Injected automatically.
   */
  layoutSize: LayoutSize
}

export type GridDiagramCellSize = "sm" | "lg"

class GridDiagramCellBase extends React.PureComponent<GridDiagramCellProps> {

  public static defaultProps: Partial<GridDiagramCellProps> = {
    color: "gray",
    icon: "check-solid",
    variant: "default"
  }

  private readonly cellID: string

  private readonly iconSizes: Record<GridDiagramCellSize, IconSize> = {
    sm: 24,
    lg: 32,
  }

  public constructor(props: GridDiagramCellProps) {
    super(props)

    this.getStyle = this.getStyle.bind(this)
    this.handleClick = this.handleClick.bind(this)

    this.cellID = generateID()
  }

  public handleClick() {
    const {
      onSelect,
      enabled
    } = this.props

    if (enabled && onSelect) {
      onSelect()
    }
  }

  public getStyle(color: string | undefined): React.CSSProperties | undefined {
    return !isGridDiagramCellColor(color) ? { backgroundColor: color } : undefined
  }

  public getFallbackLocationAssistiveLabel() {
    const {
      identifier,
      description,
      enabled,
      selected,
      translator,
    } = this.props

    if (!enabled && !selected) {
      return undefined
    }

    let label = ""

    if (identifier) {
      label += identifier
    }

    if (description) {
      if (label.length !== 0) {
        label += ", "
      }
      label += translator.lookup({
        stringID: GridDiagramCellConfig.defaultStrings.locationAssistiveLabel
      })
    }

    return label.length > 0 ? label : undefined
  }

  public getLocationAssistiveLabel() {
    const {
      identifier,
      description,
      translator,
    } = this.props

    if (!identifier || !description) {
      return this.getFallbackLocationAssistiveLabel()
    }

    const descriptionLookup = translator.lookup(description)

    return translator.lookup({
      stringID: GridDiagramCellConfig.defaultStrings.locationAssistiveLabel,
      parameters: {
        location: identifier,
        description: descriptionLookup
      },
      defaultString: `Location ${identifier}: ${descriptionLookup}`
    })
  }

  public renderIcon() {
    const {
      label,
      size = "sm",
      icon
    } = this.props

    const iconClassName = "grid-diagram-cell__icon"
      + (label ? " grid-diagram-cell__icon--padded" : "")

    return (
        <div className={iconClassName}>
          <Icon name={icon || "check-solid"} size={this.iconSizes[size]} color="gray" />
        </div>
    )
  }

  public renderRegular() {
    const {
      label,
      identifier,
      description,
      size = "sm",
      enabled,
      selected,
      translator,
    } = this.props

    const identifierClassName = "grid-diagram-cell__identifier"
      + ` grid-diagram-cell__identifier--size-${size}`

    const descriptionClassName = "grid-diagram-cell__description"
      + ` grid-diagram-cell__description--size-${size}`

    const checkboxWrapperClassName = "grid-diagram-cell__checkbox-wrapper"
      + (label ? " grid-diagram-cell__checkbox-wrapper--padded" : "")

    const checkboxLabel = this.getLocationAssistiveLabel()

    return (
      <div className={identifierClassName}>
        { identifier && (
          <span
            className="grid-diagram-cell__identifier-span"
            aria-hidden={checkboxLabel !== undefined}
          >
            {identifier}
          </span>
        )}
        { description && (
          <div className={descriptionClassName} aria-hidden={checkboxLabel !== undefined}>
            {translator.lookup(description)}
          </div>
        )}
        { (enabled || selected) && (
          <div className={checkboxWrapperClassName}>
            <input
              id={this.cellID}
              className={`grid-diagram-cell__checkbox grid-diagram-cell__checkbox--${ enabled ? "enabled" : "disabled" }`}
              type="checkbox"
              checked={selected}
              readOnly={true}
              tabIndex={!enabled ? -1 : undefined}
            />
            { checkboxLabel && (
              <label htmlFor={this.cellID} className="sr-only">
                {checkboxLabel}
              </label>
            )}
            { enabled && (
              <div className="grid-diagram-cell__checkbox-focus"/>
            )}
            <div className="grid-diagram-cell__checkbox-image"/>
          </div>
        )}
      </div>
    )
  }

  public render() {
    const {
      label,
      variant = "default",
      color,
      enabled,
    } = this.props

    let className = "grid-diagram-cell"

    if (isGridDiagramCellColor(color)) {
      className += ` grid-diagram-cell--color-${color}`
    } else if (color) {
      className += ` grid-diagram-cell--foreground-${textColor(color)}`
    }

    if (enabled) {
      className += " grid-diagram-cell--selectable"
    }

    const identifierWrapperClassName = "grid-diagram-cell__identifier-wrapper"
      + (label ? " grid-diagram-cell__identifier-wrapper--with-label" : "")

    return (
      <div
        className={className}
        style={this.getStyle(color)}
        onClick={enabled ? this.handleClick : undefined}
        data-testid={renderTestID(this.props.testID)}
      >
        {(label) && (
          <div className="grid-diagram-cell__label">
            {label}
          </div>
        )}
        <div className={identifierWrapperClassName}>
          {variant === "icon" ? (
            this.renderIcon()
          ) : (
            this.renderRegular()
          )}
        </div>
      </div>
    )
  }

}

export const GridDiagramCell = Object.assign(
  withLayoutSize(
    withTranslator(GridDiagramCellBase)
  ), {
    displayName: "GridDiagramCell"
  }
)
